import { X, AlertTriangle, Lightbulb, TrendingUp, TrendingDown, Loader2, Clock, Zap } from 'lucide-react';
import type { DevelopmentDefinition, ForecastData } from '../../api/development.js';

interface ForecastModalProps {
  upgrade: DevelopmentDefinition;
  forecast?: ForecastData;
  currentBalance: number;
  onStart: () => void;
  onClose: () => void;
  isStarting: boolean;
}

const STAT_LABELS: Record<string, string> = {
  atmosphere: 'Atmosphere',
  water: 'Water',
  temperature: 'Temperature',
  carbon: 'Carbon',
  hydrogen: 'Hydrogen',
  nitrogen: 'Nitrogen',
  oxygen: 'Oxygen',
  phosphorus: 'Phosphorus',
  sulphur: 'Sulphur',
  primitiveLife: 'Primitive Life',
  biomass: 'Biomass',
  biodiversity: 'Biodiversity',
  animalLife: 'Animal Life',
  population: 'Population',
  technology: 'Technology',
  energy: 'Energy',
  researchSpeed: 'Research',
  scanRange: 'Star Map Coverage',
  stability: 'Stability',
};

export default function ForecastModal({ upgrade, forecast, currentBalance, onStart, onClose, isStarting }: ForecastModalProps) {
  const canAfford = currentBalance >= upgrade.cost;
  const hasForecast = !!forecast && forecast.snapshots.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg max-h-[90vh] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold">{upgrade.name}</h3>
            <p className="text-xs text-slate-400">{upgrade.description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Construction info */}
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1.5 text-slate-300">
              <Clock className="w-4 h-4 text-cyan-400" />
              {upgrade.construction_days} day{upgrade.construction_days !== 1 ? 's' : ''} to build
            </span>
            <span className="flex items-center gap-1.5 text-slate-300">
              <Zap className="w-4 h-4 text-yellow-400" />
              {upgrade.cost} credits upfront
            </span>
            {upgrade.upkeep_cost > 0 && (
              <span className="flex items-center gap-1.5 text-slate-300">
                <Zap className="w-4 h-4 text-orange-400" />
                {upgrade.upkeep_cost}cr/day upkeep
              </span>
            )}
          </div>

          {/* Flavour text */}
          {upgrade.flavour_text && (
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <p className="text-sm text-slate-300 italic">"{upgrade.flavour_text}"</p>
            </div>
          )}

          {/* Forecast */}
          {hasForecast ? (
            <>
              {/* Stat changes summary */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-2">7-Day Impact</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(forecast!.statChanges)
                    .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
                    .map(([stat, change]) => (
                      <div
                        key={stat}
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                          change > 0
                            ? 'bg-green-950/30 border border-green-800/30'
                            : change < 0
                            ? 'bg-red-950/30 border border-red-800/30'
                            : 'bg-slate-800/30 border border-slate-700/30'
                        }`}
                      >
                        {change > 0 ? (
                          <TrendingUp className="w-3.5 h-3.5 text-green-400 shrink-0" />
                        ) : (
                          <TrendingDown className="w-3.5 h-3.5 text-red-400 shrink-0" />
                        )}
                        <span className="text-slate-400 text-xs">{STAT_LABELS[stat] || stat}</span>
                        <span className={`font-mono font-semibold ml-auto ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {change > 0 ? '+' : ''}{change.toFixed(1)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Daily snapshot table */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-2">Day-by-Day Projection</h4>
                <div className="space-y-1">
                  {forecast!.snapshots.slice(0, 7).map((snap) => (
                    <div
                      key={snap.day}
                      className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-800/30 transition-colors"
                    >
                      <span className="text-[10px] text-slate-500 font-mono w-8">D{snap.day}</span>
                      <div className="flex-1 flex items-center gap-1 flex-wrap">
                        {Object.entries(snap.stats)
                          .filter(([, v]) => typeof v === 'number' && v > 0.5)
                          .slice(0, 4)
                          .map(([k, v]) => (
                            <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                              {STAT_LABELS[k] || k}: {Math.round((v as number) * 10) / 10}
                            </span>
                          ))}
                      </div>
                      {snap.events.length > 0 && (
                        <span className="text-[10px] text-amber-400 shrink-0">
                          {snap.events.length} event{snap.events.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Warnings */}
              {forecast!.warnings.length > 0 && (
                <div className="space-y-1.5">
                  {forecast!.warnings.map((warning, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-red-950/20 border border-red-800/30">
                      <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                      <span className="text-xs text-red-300">{warning}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Synergies */}
              {forecast!.synergies.length > 0 && (
                <div className="space-y-1.5">
                  {forecast!.synergies.map((synergy, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-cyan-950/20 border border-cyan-800/30">
                      <Lightbulb className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                      <span className="text-xs text-cyan-300">{synergy}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-sm">Calculating forecast...</p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-slate-800 flex items-center justify-between shrink-0">
          <div className="text-xs text-slate-500">
            Total cost: <span className="text-yellow-400 font-mono">{forecast?.totalCost || upgrade.cost}</span> credits
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onStart}
              disabled={!canAfford || isStarting}
              className="px-4 py-2 rounded-lg text-sm bg-cyan-600 hover:bg-cyan-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isStarting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Start Project
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
